/**
 * TourStopFormPage - Create or edit a tour stop
 */

import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  getTourStop,
  createTourStop,
  updateTourStop,
  getTourStopUnsentCampaigns,
  type UnsentCampaign,
  deleteTourStop,
  getShows,
  getTheaters,
  getCities,
  createCity,
  createTheater,
  getAdminBookings,
  getSystemSettings,
  getTourStopFaculty,
  formatCurrency,
  type Show,
  type Theater,
  type City,
  type AdminBooking,
  type TourStopFacultyMember,
} from '../../../api/admin';
import {
  getAdminWaitlist,
  notifyWaitlistEntry,
  deleteWaitlistEntry,
  type WaitlistEntry,
} from '../../../api/events';
import { PlacesAutocomplete, type PlaceResult } from '../../../components/events/PlacesAutocomplete';
import TheaterManagementDrawer from '../../../components/admin/TheaterManagementDrawer';
import CityManagementDrawer from '../../../components/admin/CityManagementDrawer';
import ConfirmModal from '../../../components/ui/ConfirmModal';

/**
 * Searchable dropdown component
 */
function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = 'Search...',
  disabled = false,
}: {
  value: string;
  onChange: (val: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  disabled?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedLabel = options.find((o) => o.value === value)?.label || '';

  const filtered = options
    .filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => a.label.localeCompare(b.label));

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!disabled) {
            setIsOpen(!isOpen);
            setSearch('');
            setTimeout(() => inputRef.current?.focus(), 0);
          }
        }}
        className={`w-full px-4 py-2.5 border border-warm-200 rounded-lg text-left flex items-center justify-between transition-colors ${
          disabled
            ? 'bg-warm-50 text-warm-400 cursor-not-allowed'
            : 'bg-white hover:border-warm-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent'
        }`}
      >
        <span className={value ? 'text-warm-900' : 'text-warm-400'}>
          {selectedLabel || placeholder}
        </span>
        <svg className="w-4 h-4 text-warm-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d={isOpen ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'} />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-warm-200 rounded-lg shadow-lg overflow-hidden">
          <div className="p-2 border-b border-warm-100">
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Type to search..."
              className="w-full px-3 py-2 text-sm border border-warm-200 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <ul className="max-h-48 overflow-y-auto">
            {filtered.length === 0 ? (
              <li className="px-4 py-3 text-sm text-warm-400">No results found</li>
            ) : (
              filtered.map((opt) => (
                <li
                  key={opt.value}
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                    setSearch('');
                  }}
                  className={`px-4 py-2.5 text-sm cursor-pointer transition-colors ${
                    opt.value === value
                      ? 'bg-primary-50 text-primary-700 font-medium'
                      : 'text-warm-700 hover:bg-warm-50'
                  }`}
                >
                  {opt.label}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function describeUnsentCampaign(c: UnsentCampaign): string {
  const when = c.status === 'sending'
    ? 'sending now / waiting on the daily limit'
    : c.send_at
      ? `scheduled for ${new Date(c.send_at).toLocaleString('en-US', {
          timeZone: 'America/New_York',
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })} ET`
      : 'scheduled (no date set)';
  const audience = c.estimated_audience ? ` · about ${c.estimated_audience.toLocaleString('en-US')} studios` : '';
  return `${c.name} — ${when}${audience}`;
}

export default function TourStopFormPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  const cloneId = searchParams.get('clone');

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Set when deactivating a stop; holds the warning shown before its unsent emails are cancelled.
  const [deactivateWarning, setDeactivateWarning] = useState<{ campaigns: UnsentCampaign[] | null } | null>(null);

  // Filter data
  const [shows, setShows] = useState<Show[]>([]);
  const [theaters, setTheaters] = useState<Theater[]>([]);
  const [cities, setCities] = useState<City[]>([]);

  // Form state
  const [showId, setShowId] = useState('');
  const [theaterId, setTheaterId] = useState('');
  const [cityId, setCityId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [initialIsActive, setInitialIsActive] = useState(true);
  const [notes, setNotes] = useState('');
  const [matineeTime, setMatineeTime] = useState('');
  const [defaultMatineeTime, setDefaultMatineeTime] = useState('14:00');
  const [sundayEnabled, setSundayEnabled] = useState(true);
  const [mondayEnabled, setMondayEnabled] = useState(true);
  const [sundayMatineeTime, setSundayMatineeTime] = useState('13:00');
  const [mondayStartTime, setMondayStartTime] = useState('09:00');
  const [dateViewMode, setDateViewMode] = useState<'auto' | 'list' | 'calendar'>('auto');
  const [maxFaculty, setMaxFaculty] = useState(3);
  const [blockedDates, setBlockedDates] = useState<string[]>([]);
  const [isForcedClosed, setIsForcedClosed] = useState(false);

  // Related bookings (edit mode only)
  const [bookings, setBookings] = useState<AdminBooking[]>([]);
  const [isLoadingBookings, setIsLoadingBookings] = useState(false);

  // Faculty availability (edit mode only)
  const [facultyMembers, setFacultyMembers] = useState<TourStopFacultyMember[]>([]);
  const [facultySummary, setFacultySummary] = useState<{ total_certified: number; available_today: number }>({ total_certified: 0, available_today: 0 });
  const [isLoadingFaculty, setIsLoadingFaculty] = useState(false);

  // Tab state (edit mode only)
  const [activeTab, setActiveTab] = useState<'details' | 'waitlist'>('details');

  // Waitlist data (edit mode only)
  const [waitlistEntries, setWaitlistEntries] = useState<WaitlistEntry[]>([]);
  const [isLoadingWaitlist, setIsLoadingWaitlist] = useState(false);
  const [waitlistError, setWaitlistError] = useState<string | null>(null);
  const [waitlistSuccess, setWaitlistSuccess] = useState<string | null>(null);
  const [waitlistConfirmDeleteId, setWaitlistConfirmDeleteId] = useState<string | null>(null);
  const [waitlistNotifyEntry, setWaitlistNotifyEntry] = useState<WaitlistEntry | null>(null);
  const [waitlistActionLoading, setWaitlistActionLoading] = useState<string | null>(null);

  // Track original values for edit (only send changed fields)
  const [originalStartDate, setOriginalStartDate] = useState('');
  const [originalEndDate, setOriginalEndDate] = useState('');

  // Add City modal state
  const [showAddCity, setShowAddCity] = useState(false);
  const [newCityName, setNewCityName] = useState('');
  const [newCityState, setNewCityState] = useState('');
  // Google Places locality place_id — captured when a city is picked from
  // autocomplete so the backend can dedup to a canonical row. Cleared on manual edit.
  const [newCityPlaceId, setNewCityPlaceId] = useState('');
  const [isCreatingCity, setIsCreatingCity] = useState(false);

  // Add Theater modal state
  const [showAddTheater, setShowAddTheater] = useState(false);
  const [newTheaterName, setNewTheaterName] = useState('');
  const [newTheaterAddress, setNewTheaterAddress] = useState('');
  const [newTheaterZip, setNewTheaterZip] = useState('');
  const [isCreatingTheater, setIsCreatingTheater] = useState(false);
  const [theaterCreateResult, setTheaterCreateResult] = useState<{
    success: boolean;
    geocoded: boolean;
    resolvedAddress?: string;
  } | null>(null);

  // Theater management drawer state
  const [showTheaterDrawer, setShowTheaterDrawer] = useState(false);

  // City management drawer state
  const [showCityDrawer, setShowCityDrawer] = useState(false);

  useEffect(() => {
    loadFormData();
  }, [id, cloneId]);

  const loadFormData = async () => {
    try {
      setIsLoading(true);

      // Load filter options and system settings
      const [showsData, theatersData, citiesData, settingsData] = await Promise.all([
        getShows(),
        getTheaters(),
        getCities(),
        getSystemSettings().catch(() => null),
      ]);
      setShows(showsData);
      setTheaters(theatersData);
      setCities(citiesData);
      if (settingsData) {
        setDefaultMatineeTime(settingsData.default_matinee_time.slice(0, 5));
      }

      // Load existing data if editing or cloning
      const tourStopId = id || cloneId;
      if (tourStopId) {
        // Fetch tour stop and bookings in parallel (bookings only when editing, not cloning)
        const fetchBookings = id
          ? getAdminBookings({ tour_stop_id: id }).then(({ bookings: b }) => {
              setBookings(b);
              setIsLoadingBookings(false);
            }).catch(() => {
              setBookings([]);
              setIsLoadingBookings(false);
            })
          : Promise.resolve();

        const fetchFaculty = id
          ? getTourStopFaculty(id).then((data) => {
              setFacultyMembers(data.faculty);
              setFacultySummary(data.summary);
              setIsLoadingFaculty(false);
            }).catch(() => {
              setFacultyMembers([]);
              setIsLoadingFaculty(false);
            })
          : Promise.resolve();

        if (id) {
          setIsLoadingBookings(true);
          setIsLoadingFaculty(true);
        }

        const [tourStop] = await Promise.all([
          getTourStop(tourStopId),
          fetchBookings,
          fetchFaculty,
        ]);

        setShowId(tourStop.show_id);
        setTheaterId(tourStop.theater_id);
        setCityId(tourStop.city_id);
        setNotes(tourStop.notes || '');
        setIsActive(tourStop.is_active);
        setInitialIsActive(tourStop.is_active);
        if (tourStop.matinee_time) setMatineeTime(tourStop.matinee_time.slice(0, 5));
        if (tourStop.date_view_mode) setDateViewMode(tourStop.date_view_mode);
        if (tourStop.max_faculty != null) setMaxFaculty(tourStop.max_faculty);
        if (tourStop.sunday_enabled != null) setSundayEnabled(tourStop.sunday_enabled);
        if (tourStop.monday_enabled != null) setMondayEnabled(tourStop.monday_enabled);
        if (tourStop.sunday_matinee_time) setSundayMatineeTime(tourStop.sunday_matinee_time.slice(0, 5));
        if (tourStop.monday_start_time) setMondayStartTime(tourStop.monday_start_time.slice(0, 5));
        if (tourStop.blocked_dates) setBlockedDates(tourStop.blocked_dates.map((d: string) => d.slice(0, 10)).filter((d: string) => /^\d{4}-\d{2}-\d{2}$/.test(d)));
        if (tourStop.is_forced_closed != null) setIsForcedClosed(tourStop.is_forced_closed);

        // If cloning, don't copy dates
        if (id) {
          setStartDate(tourStop.start_date);
          setEndDate(tourStop.end_date);
          setOriginalStartDate(tourStop.start_date);
          setOriginalEndDate(tourStop.end_date);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load form data');
    } finally {
      setIsLoading(false);
    }
  };

  // Load waitlist count on page load, and full data when tab is active
  useEffect(() => {
    if (id) {
      loadWaitlist();
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadWaitlist = async () => {
    if (!id) return;
    try {
      setIsLoadingWaitlist(true);
      setWaitlistError(null);
      const result = await getAdminWaitlist({ tour_stop_id: id });
      setWaitlistEntries(result.data);
    } catch (err) {
      setWaitlistError(err instanceof Error ? err.message : 'Failed to load waitlist');
    } finally {
      setIsLoadingWaitlist(false);
    }
  };

  const handleWaitlistNotify = async (entryId: string) => {
    try {
      setWaitlistActionLoading(entryId);
      setWaitlistError(null);
      setWaitlistSuccess(null);
      const result = await notifyWaitlistEntry(entryId);
      if (result.email_sent) {
        setWaitlistSuccess(`Notification email sent to ${result.email}.`);
      } else {
        // The API tells us the email did NOT go out — don't imply the studio was
        // notified. Surface the reason so the admin can fix the email connection.
        const why =
          result.reason === 'no_email_account'
            ? 'no email account is connected'
            : result.reason === 'no_recipient'
            ? 'this entry has no email address'
            : 'the email provider rejected the send';
        setWaitlistError(
          `Email couldn't be sent (${why}) — the studio was NOT notified. Check the email connection in Settings.`
        );
      }
      await loadWaitlist();
    } catch (err) {
      setWaitlistError(err instanceof Error ? err.message : 'Failed to notify entry');
    } finally {
      setWaitlistActionLoading(null);
    }
  };

  const handleWaitlistDelete = async (entryId: string) => {
    try {
      setWaitlistActionLoading(entryId);
      await deleteWaitlistEntry(entryId);
      setWaitlistConfirmDeleteId(null);
      await loadWaitlist();
    } catch (err) {
      setWaitlistError(err instanceof Error ? err.message : 'Failed to remove entry');
    } finally {
      setWaitlistActionLoading(null);
    }
  };

  const getWaitlistStatusBadge = (entry: WaitlistEntry) => {
    if (entry.converted_at) {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
          Converted
        </span>
      );
    }
    if (entry.notified_at) {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-700">
          Notified
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-warning-100 text-warning-700">
        Pending
      </span>
    );
  };

  const formatWaitlistDate = (date: string) =>
    new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  const waitlistPendingCount = waitlistEntries.filter((e) => !e.notified_at && !e.converted_at).length;
  const waitlistNotifiedCount = waitlistEntries.filter((e) => e.notified_at && !e.converted_at).length;
  const waitlistConvertedCount = waitlistEntries.filter((e) => e.converted_at).length;

  const handleCreateCity = async () => {
    if (!newCityName.trim() || !newCityState.trim()) return;
    try {
      setIsCreatingCity(true);
      const city = await createCity({
        name: newCityName.trim(),
        state: newCityState.trim().toUpperCase(),
        google_place_id: newCityPlaceId.trim() || undefined,
      });
      // Resolver may return an existing canonical row — only add if new to the list.
      setCities((prev) =>
        prev.some((c) => c.id === city.id)
          ? prev
          : [...prev, city].sort((a, b) => a.name.localeCompare(b.name))
      );
      setCityId(city.id);
      setTheaterId('');
      setShowAddCity(false);
      setNewCityName('');
      setNewCityState('');
      setNewCityPlaceId('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create city');
    } finally {
      setIsCreatingCity(false);
    }
  };

  const handleCreateTheater = async () => {
    if (!newTheaterName.trim() || !cityId) return;
    if (!newTheaterAddress.trim() && !newTheaterZip.trim()) {
      setError('Please enter a street address or ZIP code so we can locate this venue');
      return;
    }
    try {
      setIsCreatingTheater(true);
      setTheaterCreateResult(null);
      const result = await createTheater({
        name: newTheaterName.trim(),
        city_id: cityId,
        address: newTheaterAddress.trim(),
        zip_code: newTheaterZip.trim() || undefined,
      });
      setTheaters((prev) => [...prev, result].sort((a, b) => a.name.localeCompare(b.name)));
      setTheaterId(result.id);

      // Show geocoding result
      const resultAny = result as unknown as { latitude?: number; geocoded_address?: string };
      const geocoded = !!resultAny.latitude;
      setTheaterCreateResult({
        success: true,
        geocoded,
        resolvedAddress: resultAny.geocoded_address,
      });

      // Auto-close after showing feedback briefly
      setTimeout(() => {
        setShowAddTheater(false);
        setNewTheaterName('');
        setNewTheaterAddress('');
        setNewTheaterZip('');
        setTheaterCreateResult(null);
      }, geocoded ? 2000 : 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create theater');
    } finally {
      setIsCreatingTheater(false);
    }
  };

  const handleTheaterDrawerUpdated = async () => {
    const theatersData = await getTheaters();
    setTheaters(theatersData);
  };

  const handleCityDrawerUpdated = async () => {
    const citiesData = await getCities();
    setCities(citiesData);
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      setIsDeleting(true);
      setDeleteError(null);
      await deleteTourStop(id);
      navigate('/admin/tour-stops');
    } catch (err) {
      // Shown next to the delete button (the page banner is far up the form), and the box stays open.
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete tour stop');
    } finally {
      setIsDeleting(false);
    }
  };

  const performSave = async () => {
    setError(null);
    try {
      setIsSaving(true);

      if (isEditing && id) {
        const updateData: Record<string, unknown> = {
          is_active: isActive,
          notes: notes.trim() || undefined,
          date_view_mode: dateViewMode,
          matinee_time: matineeTime || null,
          max_faculty: maxFaculty,
          sunday_enabled: sundayEnabled,
          monday_enabled: mondayEnabled,
          sunday_matinee_time: sundayMatineeTime || '13:00:00',
          monday_start_time: mondayStartTime || '09:00:00',
          blocked_dates: blockedDates,
          is_forced_closed: isForcedClosed,
        };
        // Only send dates if they changed (avoids false overlap detection)
        if (startDate !== originalStartDate) updateData.start_date = startDate;
        if (endDate !== originalEndDate) updateData.end_date = endDate;
        await updateTourStop(id, updateData as Parameters<typeof updateTourStop>[1]);
      } else {
        await createTourStop({
          show_id: showId,
          theater_id: theaterId,
          city_id: cityId,
          start_date: startDate,
          end_date: endDate,
          is_active: isActive,
          notes: notes.trim() || undefined,
          date_view_mode: dateViewMode,
          matinee_time: matineeTime || null,
          max_faculty: maxFaculty,
          sunday_enabled: sundayEnabled,
          monday_enabled: mondayEnabled,
          sunday_matinee_time: sundayMatineeTime || '13:00:00',
          monday_start_time: mondayStartTime || '09:00:00',
          blocked_dates: blockedDates,
          is_forced_closed: isForcedClosed,
        });
      }

      setInitialIsActive(isActive);
      navigate('/admin/tour-stops');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save tour stop');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSaving) return;
    setError(null);

    // Validation
    if (!showId) {
      setError('Please select a show');
      return;
    }
    if (!theaterId) {
      setError('Please select a theater');
      return;
    }
    if (!cityId) {
      setError('Please select a city');
      return;
    }
    if (!startDate || !endDate) {
      setError('Please enter start and end dates');
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      setError('End date must be after start date');
      return;
    }

    // Deactivating cancels the stop's unsent emails, so show what will be cancelled first.
    if (isEditing && id && initialIsActive && !isActive) {
      // Disables Save while the lookup runs so a double-click can't submit twice.
      setIsSaving(true);
      try {
        const campaigns = await getTourStopUnsentCampaigns(id);
        if (campaigns.length > 0) {
          setDeactivateWarning({ campaigns });
          return;
        }
      } catch {
        // Never skip the warning just because the lookup failed.
        setDeactivateWarning({ campaigns: null });
        return;
      } finally {
        setIsSaving(false);
      }
    }

    await performSave();
  };

  // Filter theaters by selected city
  const filteredTheaters = cityId
    ? theaters.filter((t) => t.city_id === cityId)
    : theaters;

  if (isLoading) {
    return (
      <div>
        <div className="h-10 bg-warm-200 rounded w-1/4 animate-pulse mb-8" />
        <div className="bg-white rounded-xl shadow-sm p-8 animate-pulse">
          <div className="space-y-6">
            <div className="h-10 bg-warm-100 rounded w-full" />
            <div className="h-10 bg-warm-100 rounded w-full" />
            <div className="h-10 bg-warm-100 rounded w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/admin/tour-stops"
          className="inline-flex items-center text-sm text-warm-600 hover:text-warm-900 mb-4"
        >
          <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Tour Stops
        </Link>
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-warm-900">
          {isEditing ? 'Edit Tour Stop' : cloneId ? 'Clone Tour Stop' : 'Add New Tour Stop'}
        </h1>
      </div>

      {/* Tab Bar (edit mode only) */}
      {isEditing && (
        <div className="flex border-b border-warm-200 mb-8">
          <button
            type="button"
            onClick={() => setActiveTab('details')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'details'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-warm-500 hover:text-warm-700 hover:border-warm-300'
            }`}
          >
            Details
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('waitlist')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'waitlist'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-warm-500 hover:text-warm-700 hover:border-warm-300'
            }`}
          >
            Waitlist ({waitlistEntries.length})
          </button>
        </div>
      )}

      {/* Waitlist Tab Content */}
      {isEditing && activeTab === 'waitlist' && (
        <div>
          {/* Summary */}
          <div className="flex flex-wrap gap-4 mb-6 text-sm">
            <span className="px-3 py-1.5 bg-warning-50 text-warning-700 rounded-lg font-medium">
              {waitlistPendingCount} pending
            </span>
            <span className="px-3 py-1.5 bg-success-50 text-success-700 rounded-lg font-medium">
              {waitlistNotifiedCount} notified
            </span>
            <span className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg font-medium">
              {waitlistConvertedCount} converted
            </span>
          </div>

          {waitlistError && (
            <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
              {waitlistError}
            </div>
          )}

          {waitlistSuccess && (
            <div className="mb-6 p-4 bg-success-50 border border-success-200 rounded-lg text-success-700">
              {waitlistSuccess}
            </div>
          )}

          {isLoadingWaitlist ? (
            <div className="bg-white rounded-xl shadow-sm">
              {[1, 2, 3].map((i) => (
                <div key={i} className="p-6 border-b border-warm-100 animate-pulse">
                  <div className="h-5 bg-warm-100 rounded w-1/3 mb-2" />
                  <div className="h-4 bg-warm-100 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : waitlistEntries.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm p-12 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-warm-100 flex items-center justify-center">
                <svg className="w-8 h-8 text-warm-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-warm-900 mb-2">No waitlist entries</h3>
              <p className="text-warm-600">
                No one is on the waitlist for this tour stop yet.
              </p>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block bg-white rounded-xl shadow-sm overflow-hidden">
                <table className="w-full">
                  <thead className="bg-warm-50 border-b border-warm-100">
                    <tr>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700">Name</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700">Email</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700">Phone</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700">Studio</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700">Status</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700">Signed Up</th>
                      <th className="text-right px-6 py-4 text-sm font-semibold text-warm-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-warm-100">
                    {waitlistEntries.map((entry) => (
                      <tr key={entry.id} className="hover:bg-warm-50 transition-colors">
                        <td className="px-6 py-4 text-sm text-warm-900">
                          {entry.contact_name || '--'}
                        </td>
                        <td className="px-6 py-4 text-sm text-warm-600">
                          {entry.email}
                        </td>
                        <td className="px-6 py-4 text-sm text-warm-600">
                          {entry.phone || '--'}
                        </td>
                        <td className="px-6 py-4 text-sm text-warm-600">
                          {entry.studio_name || '--'}
                        </td>
                        <td className="px-6 py-4">
                          {getWaitlistStatusBadge(entry)}
                        </td>
                        <td className="px-6 py-4 text-sm text-warm-600">
                          {formatWaitlistDate(entry.created_at)}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {!entry.converted_at && (
                              <button
                                onClick={() => setWaitlistNotifyEntry(entry)}
                                disabled={waitlistActionLoading === entry.id}
                                className="px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors disabled:opacity-50"
                              >
                                {waitlistActionLoading === entry.id ? 'Sending...' : entry.notified_at ? 'Notify Again' : 'Notify'}
                              </button>
                            )}
                            {waitlistConfirmDeleteId === entry.id ? (
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => handleWaitlistDelete(entry.id)}
                                  disabled={waitlistActionLoading === entry.id}
                                  className="px-3 py-1.5 text-sm text-danger-600 hover:text-danger-700 hover:bg-danger-50 rounded-lg transition-colors disabled:opacity-50"
                                >
                                  {waitlistActionLoading === entry.id ? 'Removing...' : 'Confirm'}
                                </button>
                                <button
                                  onClick={() => setWaitlistConfirmDeleteId(null)}
                                  className="px-3 py-1.5 text-sm text-warm-600 hover:text-warm-700 hover:bg-warm-50 rounded-lg transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setWaitlistConfirmDeleteId(entry.id)}
                                className="px-3 py-1.5 text-sm text-danger-600 hover:text-danger-700 hover:bg-danger-50 rounded-lg transition-colors"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden space-y-3">
                {waitlistEntries.map((entry) => (
                  <div
                    key={entry.id}
                    className="bg-white rounded-xl border border-warm-200 p-4"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      {getWaitlistStatusBadge(entry)}
                    </div>
                    <p className="font-medium text-warm-900">
                      {entry.contact_name || entry.email}
                    </p>
                    <p className="text-sm text-warm-600">{entry.email}</p>
                    {entry.phone && (
                      <p className="text-sm text-warm-600">{entry.phone}</p>
                    )}
                    {entry.studio_name && (
                      <p className="text-sm text-warm-500">{entry.studio_name}</p>
                    )}
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-xs text-warm-500">
                        {formatWaitlistDate(entry.created_at)}
                      </span>
                      <div className="flex items-center gap-2">
                        {!entry.converted_at && (
                          <button
                            onClick={() => setWaitlistNotifyEntry(entry)}
                            disabled={waitlistActionLoading === entry.id}
                            className="px-3 py-1.5 text-sm text-primary-600 hover:bg-primary-50 rounded-lg transition-colors disabled:opacity-50"
                          >
                            {entry.notified_at ? 'Notify Again' : 'Notify'}
                          </button>
                        )}
                        {waitlistConfirmDeleteId === entry.id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleWaitlistDelete(entry.id)}
                              disabled={waitlistActionLoading === entry.id}
                              className="px-3 py-1.5 text-sm text-danger-600 hover:bg-danger-50 rounded-lg transition-colors disabled:opacity-50"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => setWaitlistConfirmDeleteId(null)}
                              className="px-3 py-1.5 text-sm text-warm-600 hover:bg-warm-50 rounded-lg transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setWaitlistConfirmDeleteId(entry.id)}
                            className="px-3 py-1.5 text-sm text-danger-600 hover:bg-danger-50 rounded-lg transition-colors"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Details Tab Content */}
      <form onSubmit={handleSubmit} style={{ display: activeTab === 'details' || !isEditing ? undefined : 'none' }}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Booking Status */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-warm-900">Booking Status</h3>
                  <p className="text-sm text-warm-500 mt-1">
                    {isForcedClosed
                      ? 'Bookings are closed. Studios must submit a waitlist request.'
                      : 'Bookings are open. Studios can book directly (subject to auto-close within 21 days).'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsForcedClosed(!isForcedClosed)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                    isForcedClosed ? 'bg-danger-500' : 'bg-success-500'
                  }`}
                >
                  <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    isForcedClosed ? 'translate-x-5' : 'translate-x-0'
                  }`} />
                </button>
              </div>
            </div>

            {/* Show Selection */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-warm-900 mb-4">
                Show Information
              </h2>

              {error && (
                <div className="mb-4 p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                {/* Show */}
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    Show *
                  </label>
                  <select
                    value={showId}
                    onChange={(e) => setShowId(e.target.value)}
                    required
                    className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Select a show</option>
                    {shows
                      // Active shows only; when editing, keep the current show even
                      // if it has since been deactivated so the selection stays valid.
                      .filter((show) => show.is_active || show.id === showId)
                      .map((show) => (
                        <option key={show.id} value={show.id}>
                          {show.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-warm-900 mb-4">
                Location
              </h2>

              <div className="space-y-4">
                {/* City */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-warm-700">
                      City *
                    </label>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setShowCityDrawer(true)}
                        className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                      >
                        Manage Cities
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowAddCity(!showAddCity)}
                        className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                      >
                        {showAddCity ? 'Cancel' : '+ Add New City'}
                      </button>
                    </div>
                  </div>

                  {showAddCity ? (
                    <div className="p-4 border border-primary-200 bg-primary-50 rounded-lg space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="col-span-2">
                          <label className="block text-xs font-medium text-warm-600 mb-1">City</label>
                          <PlacesAutocomplete
                            mode="city"
                            value={newCityName}
                            onInputChange={(textVal) => { setNewCityName(textVal); setNewCityState(''); setNewCityPlaceId(''); }}
                            onPlaceSelected={(place: PlaceResult) => {
                              const cityName = place.addressComponents?.city || place.name;
                              if (cityName) setNewCityName(cityName);
                              if (place.addressComponents?.state) setNewCityState(place.addressComponents.state);
                              // Capture the canonical Google key so the backend dedups.
                              setNewCityPlaceId(place.placeId || '');
                            }}
                            placeholder="Start typing a city…"
                            className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-warm-600 mb-1">State</label>
                          <input
                            type="text"
                            value={newCityState}
                            onChange={(e) => { setNewCityState(e.target.value.toUpperCase()); setNewCityPlaceId(''); }}
                            placeholder="NY"
                            maxLength={2}
                            className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm uppercase"
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleCreateCity}
                        disabled={!newCityName.trim() || !newCityState.trim() || isCreatingCity}
                        className="px-4 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
                      >
                        {isCreatingCity ? 'Creating...' : 'Create City'}
                      </button>
                    </div>
                  ) : (
                    <SearchableSelect
                      value={cityId}
                      onChange={(val) => {
                        setCityId(val);
                        setTheaterId('');
                      }}
                      options={cities.map((city) => ({
                        value: city.id,
                        label: `${city.name}, ${city.state}`,
                      }))}
                      placeholder="Select a city"
                    />
                  )}
                </div>

                {/* Theater */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-warm-700">
                      Theater / Venue *
                    </label>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setShowTheaterDrawer(true)}
                        className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                      >
                        Manage Theaters
                      </button>
                      {cityId && (
                        <button
                          type="button"
                          onClick={() => setShowAddTheater(!showAddTheater)}
                          className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                        >
                          {showAddTheater ? 'Cancel' : '+ Add New Theater'}
                        </button>
                      )}
                    </div>
                  </div>

                  {showAddTheater && cityId ? (
                    <div className="p-4 border border-primary-200 bg-primary-50 rounded-lg space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-warm-600 mb-1">Search Venue</label>
                        <PlacesAutocomplete
                          mode="studio"
                          onPlaceSelected={(place: PlaceResult) => {
                            if (place.name) setNewTheaterName(place.name);
                            const street = place.addressComponents?.street || place.formattedAddress;
                            if (street) setNewTheaterAddress(street);
                            if (place.addressComponents?.zip) setNewTheaterZip(place.addressComponents.zip);
                          }}
                          placeholder="Search by venue name or address..."
                          className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                        />
                        <p className="mt-1 text-xs text-warm-500">Or enter manually below</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-warm-600 mb-1">Venue Name *</label>
                        <input
                          type="text"
                          value={newTheaterName}
                          onChange={(e) => setNewTheaterName(e.target.value)}
                          placeholder="e.g. Richard Rodgers Theatre"
                          className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-warm-600 mb-1">Street Address</label>
                        <PlacesAutocomplete
                          mode="address"
                          onPlaceSelected={(place: PlaceResult) => {
                            setNewTheaterAddress(place.addressComponents?.street || place.formattedAddress);
                            if (place.addressComponents?.zip) setNewTheaterZip(place.addressComponents.zip);
                            if (place.name && !newTheaterName.trim()) setNewTheaterName(place.name);
                          }}
                          defaultValue={newTheaterAddress}
                          placeholder="e.g. 226 W 46th St"
                          className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-warm-600 mb-1">ZIP Code</label>
                        <input
                          type="text"
                          value={newTheaterZip}
                          onChange={(e) => setNewTheaterZip(e.target.value.toUpperCase().replace(/[^A-Z0-9 ]/g, '').slice(0, 10))}
                          placeholder="e.g. 10036 or L8P 4S8"
                          maxLength={10}
                          className="w-[120px] px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                        />
                      </div>
                      <p className="text-xs text-warm-500">
                        Enter at least an address or ZIP code. This is used to calculate distance-based pricing for studios. The more specific, the better.
                      </p>

                      {theaterCreateResult && (
                        <div className={`p-3 rounded-lg text-sm ${
                          theaterCreateResult.geocoded
                            ? 'bg-success-50 border border-success-200 text-success-700'
                            : 'bg-warning-50 border border-warning-200 text-warning-700'
                        }`}>
                          {theaterCreateResult.geocoded ? (
                            <>
                              <span className="font-medium">Location verified</span>
                              {theaterCreateResult.resolvedAddress && (
                                <span> — {theaterCreateResult.resolvedAddress}</span>
                              )}
                            </>
                          ) : (
                            <>
                              <span className="font-medium">Theater created, but location could not be verified.</span>
                              {' '}Distance-based pricing won't work until coordinates are set. Try adding a more specific address.
                            </>
                          )}
                        </div>
                      )}

                      {!theaterCreateResult && (
                        <button
                          type="button"
                          onClick={handleCreateTheater}
                          disabled={!newTheaterName.trim() || (!newTheaterAddress.trim() && !newTheaterZip.trim()) || isCreatingTheater}
                          className="px-4 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
                        >
                          {isCreatingTheater ? 'Locating & Creating...' : 'Create Theater'}
                        </button>
                      )}
                    </div>
                  ) : (
                    <>
                      <SearchableSelect
                        value={theaterId}
                        onChange={(val) => setTheaterId(val)}
                        disabled={!cityId}
                        options={filteredTheaters.map((theater) => ({
                          value: theater.id,
                          label: theater.name,
                        }))}
                        placeholder={cityId ? 'Select a theater' : 'Select a city first'}
                      />
                      {cityId && filteredTheaters.length === 0 && !showAddTheater && (
                        <p className="mt-1 text-sm text-warning-600">
                          No theaters found for this city.{' '}
                          <button
                            type="button"
                            onClick={() => setShowAddTheater(true)}
                            className="text-primary-600 hover:text-primary-700 font-medium underline"
                          >
                            Add one now
                          </button>
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Dates */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-warm-900 mb-4">
                Tour Dates
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Start Date */}
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    required
                    className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  {startDate && (
                    <p className="text-xs text-warm-500 mt-1">
                      {new Date(startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                  )}
                </div>

                {/* End Date */}
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    End Date *
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    required
                    min={startDate}
                    className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  {endDate && (
                    <p className="text-xs text-warm-500 mt-1">
                      {new Date(endDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                  )}
                </div>
              </div>

              {/* Matinee Time */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-warm-700 mb-1">
                  Matinee Time (optional)
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="time"
                    value={matineeTime}
                    onChange={(e) => setMatineeTime(e.target.value)}
                    className="w-48 px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  {matineeTime && (
                    <button
                      type="button"
                      onClick={() => setMatineeTime('')}
                      className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                    >
                      Reset to default
                    </button>
                  )}
                </div>
                <p className="mt-1 text-sm text-warm-500">
                  {matineeTime
                    ? 'Custom matinee time for this tour stop.'
                    : `Using system default: ${new Date(`2000-01-01T${defaultMatineeTime}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}.`
                  }
                  {' '}When the matinee is early enough (at or before the threshold), both class sessions run simultaneously instead of back-to-back.
                </p>
              </div>

              {/* Class Day Scheduling */}
              <div className="mt-6 p-4 bg-warm-50 rounded-lg">
                <h3 className="text-sm font-semibold text-warm-800 mb-3">Class Day Scheduling</h3>

                {/* Sunday */}
                <div className="mb-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sundayEnabled}
                      onChange={(e) => setSundayEnabled(e.target.checked)}
                      className="w-5 h-5 rounded border-warm-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-warm-700">Sunday Classes Enabled</span>
                  </label>
                  {sundayEnabled && (
                    <div className="mt-2 ml-8">
                      <label className="block text-sm text-warm-600 mb-1">Sunday Matinee Time</label>
                      <input
                        type="time"
                        value={sundayMatineeTime}
                        onChange={(e) => setSundayMatineeTime(e.target.value)}
                        className="w-48 px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                      <p className="mt-1 text-xs text-warm-500">
                        Matinee time for Sunday classes (default: 1:00 PM).
                      </p>
                    </div>
                  )}
                </div>

                {/* Monday */}
                <div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={mondayEnabled}
                      onChange={(e) => setMondayEnabled(e.target.checked)}
                      className="w-5 h-5 rounded border-warm-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-warm-700">Monday Classes Enabled</span>
                  </label>
                  {mondayEnabled && (
                    <div className="mt-2 ml-8">
                      <label className="block text-sm text-warm-600 mb-1">Monday Start Time</label>
                      <input
                        type="time"
                        value={mondayStartTime}
                        onChange={(e) => setMondayStartTime(e.target.value)}
                        className="w-48 px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                      <p className="mt-1 text-xs text-warm-500">
                        Start time for Monday classes (default: 9:00 AM).
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Blocked Dates */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-warm-700 mb-2">
                  Blocked Dates
                </label>
                <p className="text-xs text-warm-500 mb-3">
                  Classes will not be available on these dates.
                </p>

                {/* List of blocked dates */}
                {blockedDates.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {blockedDates.sort().map((date) => (
                      <div key={date} className="flex items-center justify-between bg-warm-50 px-3 py-2 rounded-lg">
                        <span className="text-sm text-warm-700">
                          {new Date(date.slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                        <button
                          type="button"
                          onClick={() => setBlockedDates(prev => prev.filter(d => d !== date))}
                          className="text-warm-400 hover:text-danger-500 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add date picker */}
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    id="blocked-date-input"
                    min={startDate || undefined}
                    max={endDate || undefined}
                    className="px-3 py-2 border border-warm-200 rounded-lg text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const input = document.getElementById('blocked-date-input') as HTMLInputElement;
                      const val = input?.value;
                      if (val && /^\d{4}-\d{2}-\d{2}$/.test(val) && !blockedDates.includes(val)) {
                        setBlockedDates(prev => [...prev, val]);
                        input.value = '';
                      }
                    }}
                    className="px-3 py-2 text-sm font-medium text-primary-600 border border-primary-300 rounded-lg hover:bg-primary-50 transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Faculty Capacity */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-warm-700 mb-1">
                  Faculty Capacity
                </label>
                <input
                  type="number"
                  value={maxFaculty}
                  onChange={(e) => setMaxFaculty(Math.max(1, parseInt(e.target.value) || 1))}
                  min={1}
                  className="w-32 px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <p className="mt-1 text-sm text-warm-500">
                  Maximum number of faculty members available per date. When all spots are booked, the date shows as sold out.
                </p>
              </div>
            </div>

            {/* Date View Mode */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-warm-900 mb-4">
                Date View Mode
              </h2>
              <select
                value={dateViewMode}
                onChange={(e) => setDateViewMode(e.target.value as 'auto' | 'list' | 'calendar')}
                className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="auto">Auto (Recommended)</option>
                <option value="list">List View</option>
                <option value="calendar">Calendar View</option>
              </select>
              <p className="mt-2 text-sm text-warm-500">
                Controls how dates are displayed to studios. Auto switches between list (8 or fewer dates) and calendar (more than 8 dates).
              </p>
            </div>

            {/* Notes */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-warm-900 mb-4">
                Internal Notes
              </h2>

              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                placeholder="Any internal notes about this tour stop..."
              />
            </div>

            {/* Bookings (if editing) */}
            {isEditing && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-warm-900">
                    Bookings{!isLoadingBookings && ` (${bookings.length})`}
                  </h2>
                  <Link
                    to="/admin/bookings/new"
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    + New Booking
                  </Link>
                </div>

                {isLoadingBookings ? (
                  <div className="space-y-3">
                    {[1, 2].map((i) => (
                      <div key={i} className="p-4 bg-warm-50 rounded-lg animate-pulse">
                        <div className="h-4 bg-warm-200 rounded w-1/3 mb-2" />
                        <div className="h-3 bg-warm-200 rounded w-1/2" />
                      </div>
                    ))}
                  </div>
                ) : bookings.length === 0 ? (
                  <div className="text-center py-8">
                    <svg className="w-10 h-10 text-warm-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 010 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 010-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375z" />
                    </svg>
                    <p className="text-sm text-warm-500 mb-2">No bookings yet</p>
                    <Link
                      to="/admin/bookings/new"
                      className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                    >
                      Create a booking
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {bookings.map((booking) => {
                      const statusStyles: Record<string, string> = {
                        pending: 'bg-amber-100 text-amber-700',
                        confirmed: 'bg-blue-100 text-blue-700',
                        completed: 'bg-success-100 text-success-700',
                        cancelled: 'bg-warm-200 text-warm-600',
                      };
                      return (
                        <div
                          key={booking.id}
                          className="p-4 bg-warm-50 rounded-lg"
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                            <div className="min-w-0">
                              <p className="font-medium text-warm-900 truncate">
                                {booking.studio?.name || 'Unknown Studio'}
                              </p>
                              <p className="text-sm text-warm-500 truncate">
                                {booking.studio?.primary_contact?.email || ''}
                              </p>
                              {booking.classes && booking.classes.length > 0 && (
                                <p className="text-xs text-warm-400 mt-0.5">
                                  {booking.classes
                                    .filter((c) => c.status !== 'cancelled')
                                    .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date))
                                    .map((c) => new Date(c.scheduled_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }))
                                    .join(', ')}
                                </p>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-sm shrink-0">
                              {booking.classes && (
                                <span className="text-warm-600">
                                  {booking.classes.length} class{booking.classes.length !== 1 ? 'es' : ''}
                                </span>
                              )}
                              <span className="font-medium text-warm-900">
                                {formatCurrency(booking.total_amount_cents)}
                              </span>
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                                statusStyles[booking.status] || 'bg-warm-200 text-warm-600'
                              }`}>
                                {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                              </span>
                              <Link
                                to={`/admin/bookings/${booking.id}`}
                                className="text-primary-600 hover:text-primary-700 font-medium"
                              >
                                View
                              </Link>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Available Faculty (if editing) */}
            {isEditing && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-warm-900">
                    Available Faculty
                  </h2>
                  {!isLoadingFaculty && (
                    <span className="text-sm text-warm-500">
                      {facultySummary.total_certified} certified, {facultySummary.available_today} available
                    </span>
                  )}
                </div>

                {isLoadingFaculty ? (
                  <div className="space-y-3">
                    {[1, 2].map((i) => (
                      <div key={i} className="p-4 bg-warm-50 rounded-lg animate-pulse">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-warm-200" />
                          <div className="flex-1">
                            <div className="h-4 bg-warm-200 rounded w-1/3 mb-2" />
                            <div className="h-3 bg-warm-200 rounded w-1/2" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : facultyMembers.length === 0 ? (
                  <div className="text-center py-8">
                    <svg className="w-10 h-10 text-warm-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <p className="text-sm text-warm-500">No certified faculty for this show</p>
                  </div>
                ) : (
                  <>
                    {/* Desktop table */}
                    <div className="hidden md:block overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-warm-100">
                            <th className="text-left py-2 pr-4 font-semibold text-warm-700 whitespace-nowrap">Faculty</th>
                            <th className="text-left py-2 px-3 font-semibold text-warm-700 whitespace-nowrap">Stripe</th>
                            {facultyMembers[0]?.dates.map((d) => (
                              <th key={d.date} className="text-center py-2 px-1.5 font-semibold text-warm-700 whitespace-nowrap">
                                {new Date(d.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-warm-50">
                          {facultyMembers.map((member) => (
                            <tr key={member.id} className="hover:bg-warm-50">
                              <td className="py-2 pr-4">
                                <div className="flex items-center gap-2">
                                  {member.headshot_url ? (
                                    <img src={member.headshot_url} alt={member.name} className="w-7 h-7 rounded-full object-cover" />
                                  ) : (
                                    <div className="w-7 h-7 rounded-full bg-warm-200 flex items-center justify-center">
                                      <span className="text-warm-600 text-xs font-medium">{member.name.charAt(0)}</span>
                                    </div>
                                  )}
                                  <span className="font-medium text-warm-900 whitespace-nowrap">{member.name}</span>
                                </div>
                              </td>
                              <td className="py-2 px-3">
                                {member.stripe_account_status === 'enabled' ? (
                                  <span className="text-success-600 text-xs font-medium">Enabled</span>
                                ) : member.stripe_account_status === 'restricted' ? (
                                  <span className="text-warning-600 text-xs font-medium">Restricted</span>
                                ) : member.stripe_account_status ? (
                                  <span className="text-primary-600 text-xs font-medium">Pending</span>
                                ) : (
                                  <span className="text-warm-400 text-xs">None</span>
                                )}
                              </td>
                              {member.dates.map((d) => (
                                <td key={d.date} className="py-2 px-1.5 text-center">
                                  <div className="relative group inline-block">
                                    <span
                                      className={`inline-block w-3 h-3 rounded-full ${
                                        d.status === 'available'
                                          ? 'bg-success-500'
                                          : d.status === 'blocked'
                                          ? 'bg-danger-500'
                                          : 'bg-primary-500'
                                      }`}
                                    />
                                    {d.status === 'assigned' && d.assigned_to && (
                                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block z-10 bg-warm-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                                        {d.assigned_to.booking_number} - {d.assigned_to.studio_name}
                                      </div>
                                    )}
                                    {d.status === 'blocked' && (
                                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block z-10 bg-warm-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                                        Blocked
                                      </div>
                                    )}
                                    {d.status === 'available' && (
                                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block z-10 bg-warm-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                                        Available
                                      </div>
                                    )}
                                  </div>
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile cards */}
                    <div className="md:hidden space-y-3">
                      {facultyMembers.map((member) => (
                        <div key={member.id} className="p-3 bg-warm-50 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            {member.headshot_url ? (
                              <img src={member.headshot_url} alt={member.name} className="w-8 h-8 rounded-full object-cover" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-warm-200 flex items-center justify-center">
                                <span className="text-warm-600 text-xs font-medium">{member.name.charAt(0)}</span>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-warm-900 text-sm truncate">{member.name}</p>
                              <p className="text-xs text-warm-500">
                                Stripe: {member.stripe_account_status === 'enabled' ? 'Enabled' : member.stripe_account_status === 'restricted' ? 'Restricted' : member.stripe_account_status ? 'Pending' : 'None'}
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {member.dates.map((d) => (
                              <div key={d.date} className="flex items-center gap-1 text-xs text-warm-600">
                                <span
                                  className={`inline-block w-2.5 h-2.5 rounded-full ${
                                    d.status === 'available'
                                      ? 'bg-success-500'
                                      : d.status === 'blocked'
                                      ? 'bg-danger-500'
                                      : 'bg-primary-500'
                                  }`}
                                />
                                {new Date(d.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Legend */}
                    <div className="flex items-center gap-4 mt-3 text-xs text-warm-500">
                      <div className="flex items-center gap-1">
                        <span className="inline-block w-2.5 h-2.5 rounded-full bg-success-500" />
                        Available
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="inline-block w-2.5 h-2.5 rounded-full bg-danger-500" />
                        Blocked
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="inline-block w-2.5 h-2.5 rounded-full bg-primary-500" />
                        Assigned
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="order-first lg:order-none space-y-6">
            {/* Status */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-warm-900 mb-4">
                Status
              </h2>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="w-5 h-5 rounded border-warm-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-warm-700">Tour stop is active</span>
              </label>
              <p className="mt-2 text-sm text-warm-500">
                Inactive tour stops are hidden from studios and can't be booked. Any of this stop's emails that haven't gone out are cancelled when you deactivate it.
              </p>
            </div>

            {/* Selected Show Preview */}
            {showId && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-warm-900 mb-4">
                  Selected Show
                </h2>
                {(() => {
                  const show = shows.find((s) => s.id === showId);
                  if (!show) return null;
                  return (
                    <div className="flex items-center gap-3">
                      {show.logo_url ? (
                        <img
                          src={show.logo_url}
                          alt={show.name}
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center">
                          <span className="text-primary-600 font-bold text-lg">
                            {show.name.charAt(0)}
                          </span>
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-warm-900">{show.name}</p>
                        <p className="text-sm text-warm-500">
                          ${(show.default_base_price_cents / 100).toFixed(0)} per class
                        </p>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Actions */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={isSaving}
                  className="w-full btn-primary"
                >
                  {isSaving
                    ? 'Saving...'
                    : isEditing
                    ? 'Save Changes'
                    : 'Create Tour Stop'}
                </button>
                <Link
                  to="/admin/tour-stops"
                  className="block w-full text-center px-4 py-2.5 border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 transition-colors"
                >
                  Cancel
                </Link>

                {isEditing && (
                  <>
                    <hr className="border-warm-200" />
                    {showDeleteConfirm ? (
                      <div className="p-3 bg-danger-50 border border-danger-200 rounded-lg">
                        <p className="text-sm text-danger-700 font-medium mb-2">
                          Are you sure? This cannot be undone.
                        </p>
                        {deleteError && (
                          <div className="mb-3 p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
                            {deleteError}
                          </div>
                        )}
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={handleDelete}
                            disabled={isDeleting}
                            className="flex-1 px-3 py-1.5 bg-danger-600 text-white text-sm rounded-lg hover:bg-danger-700 disabled:opacity-50 transition-colors"
                          >
                            {isDeleting ? 'Deleting...' : 'Yes, Delete'}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowDeleteConfirm(false);
                              setDeleteError(null);
                            }}
                            className="flex-1 px-3 py-1.5 border border-warm-300 text-warm-700 text-sm rounded-lg hover:bg-warm-50 transition-colors"
                          >
                            No, Keep
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setDeleteError(null);
                          setShowDeleteConfirm(true);
                        }}
                        className="w-full px-4 py-2.5 text-danger-600 hover:text-danger-700 hover:bg-danger-50 rounded-lg transition-colors text-sm font-medium"
                      >
                        Delete Tour Stop
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </form>

      <TheaterManagementDrawer
        isOpen={showTheaterDrawer}
        onClose={() => setShowTheaterDrawer(false)}
        onTheaterUpdated={handleTheaterDrawerUpdated}
      />

      <CityManagementDrawer
        isOpen={showCityDrawer}
        onClose={() => setShowCityDrawer(false)}
        onCityUpdated={handleCityDrawerUpdated}
      />

      {deactivateWarning && (
        <ConfirmModal
          title="Deactivate this tour stop?"
          message={
            deactivateWarning.campaigns ? (
              <div className="space-y-2">
                <ul className="list-disc pl-5 space-y-1">
                  {deactivateWarning.campaigns.map((c) => (
                    <li key={c.id}>{describeUnsentCampaign(c)}</li>
                  ))}
                </ul>
                <p>
                  These emails will be cancelled. If you reactivate the stop later, they won't restart on their own.
                </p>
              </div>
            ) : (
              "We couldn't check this stop for scheduled emails. Any of its emails that haven't gone out will be cancelled when you deactivate it."
            )
          }
          confirmLabel="Deactivate and cancel emails"
          confirmVariant="danger"
          onConfirm={async () => {
            setDeactivateWarning(null);
            await performSave();
          }}
          onCancel={() => setDeactivateWarning(null)}
        />
      )}

      {/* Confirm before notifying — the action emails a real studio contact, so
          preview who/what is sent before firing (SDA-205). */}
      {waitlistNotifyEntry && (
        <ConfirmModal
          title={waitlistNotifyEntry.notified_at ? 'Send notification again?' : 'Notify this studio?'}
          message={`Send a "a spot may be available" email to ${
            waitlistNotifyEntry.contact_name || waitlistNotifyEntry.email
          } at ${waitlistNotifyEntry.email}${
            waitlistNotifyEntry.show_name ? ` for ${waitlistNotifyEntry.show_name}` : ''
          }? They'll get a link to book this tour stop.`}
          confirmLabel="Send email"
          confirmVariant="primary"
          onConfirm={() => {
            const entryId = waitlistNotifyEntry.id;
            setWaitlistNotifyEntry(null);
            void handleWaitlistNotify(entryId);
          }}
          onCancel={() => setWaitlistNotifyEntry(null)}
        />
      )}
    </div>
  );
}
